import { formatCep } from "./cep";
import { formatDocument } from "./documents";
import { DomainError } from "./errors";
import { formatBRL } from "./money";
import { gtinCheckDigit } from "./product";

/**
 * Etiquetas customizadas (T3.6, RF-CAD-06). O modelo é declarativo: tamanho da etiqueta e da folha em décimos de mm
 * (inteiros), elementos com posição numérica e campos de uma LISTA BRANCA por fonte. Nada é interpretado como
 * template: texto fixo é literal e o valor de um campo é só texto. No ZPL todo texto sai em hexadecimal (`^FH`), sem
 * como injetar comandos na impressora.
 */

export const LABEL_SOURCES = ["products", "contacts"] as const;
export type LabelSource = (typeof LABEL_SOURCES)[number];
export const LABEL_SOURCE_LABELS: Record<LabelSource, string> = {
  products: "Produtos",
  contacts: "Contatos (endereços)",
};

export const LABEL_FIELDS = {
  products: {
    name: "Nome",
    sku: "SKU",
    gtin: "GTIN/EAN",
    price: "Preço de venda",
    unit: "Unidade",
    ncm: "NCM",
    location: "Localização",
    short_description: "Descrição curta",
  },
  contacts: {
    name: "Nome",
    trade_name: "Fantasia",
    code: "Código",
    document: "CPF/CNPJ",
    phone: "Telefone",
    address_line1: "Logradouro, número e complemento",
    district: "Bairro",
    city_uf: "Cidade/UF",
    cep: "CEP",
    full_address: "Endereço completo",
  },
} as const satisfies Record<LabelSource, Record<string, string>>;
export type LabelField = keyof (typeof LABEL_FIELDS)["products"] | keyof (typeof LABEL_FIELDS)["contacts"];
export const LABEL_FIELD_KEYS: readonly string[] = [
  ...new Set([...Object.keys(LABEL_FIELDS.products), ...Object.keys(LABEL_FIELDS.contacts)]),
];
export const isLabelField = (source: LabelSource, field: string): boolean =>
  Object.hasOwn(LABEL_FIELDS[source], field);

/** Papel da folha em décimos de mm. */
export const LABEL_PAPERS = {
  A4: { width: 2100, height: 2970 },
  LETTER: { width: 2159, height: 2794 },
} as const;
export type LabelPaper = keyof typeof LABEL_PAPERS;
export const LABEL_PAPER_LABELS: Record<LabelPaper, string> = { A4: "A4", LETTER: "Carta" };

export type LabelAlign = "left" | "center" | "right";
type Box = { x: number; y: number; w: number; h: number };
export type LabelElement =
  | (Box & { type: "text"; text: string; fontSize: number; bold: boolean; align: LabelAlign })
  | (Box & {
      type: "field";
      field: string;
      prefix: string;
      fontSize: number;
      bold: boolean;
      align: LabelAlign;
      maxLines: number;
    })
  | (Box & { type: "barcode"; field: string; symbology: "ean13" | "code128"; showText: boolean })
  | { type: "qrcode"; field: string; x: number; y: number; size: number }
  | (Box & { type: "line"; thickness: number });
export type LabelPage =
  | {
      kind: "sheet";
      paper: LabelPaper;
      columns: number;
      rows: number;
      marginTop: number;
      marginLeft: number;
      gapX: number;
      gapY: number;
    }
  | { kind: "roll" };
export type LabelLayout = {
  /** Largura e altura da etiqueta em décimos de mm. */
  width: number;
  height: number;
  page: LabelPage;
  zplDpi: 203 | 300;
  elements: LabelElement[];
};

export const LABEL_LIMITS = {
  minSize: 100,
  maxSize: 3000,
  maxElements: 30,
  maxColumns: 10,
  maxRows: 40,
  minFont: 5,
  maxFont: 48,
  maxLines: 4,
  maxText: 120,
  maxPrefix: 40,
  maxQrData: 300,
} as const;
/** Por impressão: registros distintos, cópias por registro e etiquetas no total. */
export const MAX_LABEL_RECORDS = 200;
export const MAX_LABEL_COPIES = 100;
export const MAX_LABELS_PER_PRINT = 2_000;
/** Até isso o arquivo volta na hora; acima, vai para a fila (job print_labels). */
export const LABELS_SYNC_MAX = 300;
export const MAX_LABEL_TEMPLATES = 100;
/**
 * Teto do arquivo gerado (PDF ou ZPL), em bytes: um modelo com dezenas de QR Codes/campos longos × 2.000 etiquetas
 * passaria de centenas de MB e derrubaria o Worker (128 MB). O uso real (gôndola, endereço, rolo) fica abaixo de
 * 2 MB nas 2.000 etiquetas. Acima: 422 `labels_too_large` (revisão de segurança da T3.6).
 */
export const LABELS_MAX_OUTPUT_BYTES = 8 * 1024 * 1024;
export const labelsTooLarge = (): never => {
  throw new DomainError(
    "labels_too_large",
    "O arquivo das etiquetas ficaria grande demais. Imprima menos etiquetas por vez ou simplifique o modelo " +
      "(menos códigos e campos).",
  );
};

const bad = (msg: string): never => {
  throw new DomainError("invalid_label_layout", msg);
};

/** Regras que dependem de mais de um campo (o formato vem do Zod): limites, campos da fonte e caber na folha. */
export function validateLabelLayout(source: LabelSource, l: LabelLayout): void {
  if (l.elements.length < 1) bad("Inclua pelo menos um elemento na etiqueta.");
  l.elements.forEach((e, i) => {
    const n = `Elemento ${i + 1}`;
    const box = e.type === "qrcode" ? { x: e.x, y: e.y, w: e.size, h: e.size } : e;
    if (box.x + box.w > l.width || box.y + box.h > l.height) bad(`${n} sai da etiqueta.`);
    if ("field" in e && !isLabelField(source, e.field)) bad(`${n}: campo indisponível para esta fonte.`);
    if (e.type === "barcode" && e.symbology === "ean13" && source === "products" && e.field !== "gtin")
      bad(`${n}: EAN-13 só com o campo GTIN/EAN.`);
  });
  if (l.page.kind === "sheet") {
    const paper = LABEL_PAPERS[l.page.paper];
    const { columns, rows, marginLeft, marginTop, gapX, gapY } = l.page;
    if (marginLeft + columns * l.width + (columns - 1) * gapX > paper.width)
      bad("As colunas não cabem na largura da folha.");
    if (marginTop + rows * l.height + (rows - 1) * gapY > paper.height)
      bad("As linhas não cabem na altura da folha.");
  }
}

export const labelsPerPage = (l: LabelLayout) => (l.page.kind === "sheet" ? l.page.columns * l.page.rows : 1);

/** Canto superior esquerdo de cada etiqueta na página (décimos de mm), na ordem de impressão (linha a linha). */
export function labelSlots(l: LabelLayout): { x: number; y: number }[] {
  if (l.page.kind === "roll") return [{ x: 0, y: 0 }];
  const out: { x: number; y: number }[] = [];
  for (let r = 0; r < l.page.rows; r++)
    for (let c = 0; c < l.page.columns; c++)
      out.push({
        x: l.page.marginLeft + c * (l.width + l.page.gapX),
        y: l.page.marginTop + r * (l.height + l.page.gapY),
      });
  return out;
}

/** Tamanho da página em décimos de mm (folha ou a própria etiqueta no rolo). */
export const labelPageSize = (l: LabelLayout) =>
  l.page.kind === "sheet" ? LABEL_PAPERS[l.page.paper] : { width: l.width, height: l.height };

// ---------- valores dos campos ----------

export type LabelValues = Readonly<Record<string, string>>;
export type ProductLabelInput = {
  name: string;
  sku: string | null;
  gtin: string | null;
  priceCents: number;
  unit: string;
  ncm: string | null;
  location: string | null;
  shortDescription: string | null;
};
export type ContactLabelInput = {
  name: string;
  tradeName: string | null;
  code: string | null;
  document: string | null;
  phone: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  uf: string | null;
  cep: string | null;
};

const s = (v: string | null | undefined) => (v ?? "").trim();
const join = (sep: string, ...parts: (string | null | undefined)[]) => parts.map(s).filter(Boolean).join(sep);
/** NCM com pontos (0000.00.00). */
const ncmText = (n: string) => (/^\d{8}$/.test(n) ? `${n.slice(0, 4)}.${n.slice(4, 6)}.${n.slice(6)}` : n);

export function productLabelValues(p: ProductLabelInput): LabelValues {
  return {
    name: s(p.name),
    sku: s(p.sku),
    gtin: s(p.gtin),
    price: formatBRL(p.priceCents),
    unit: s(p.unit),
    ncm: ncmText(s(p.ncm)),
    location: s(p.location),
    short_description: s(p.shortDescription),
  };
}

export function contactLabelValues(c: ContactLabelInput): LabelValues {
  const line1 = join(", ", c.street, c.number, c.complement);
  const cityUf = join("/", c.city, c.uf);
  const cep = s(c.cep) ? formatCep(s(c.cep)) : "";
  return {
    name: s(c.name),
    trade_name: s(c.tradeName),
    code: s(c.code),
    document: s(c.document) ? formatDocument(s(c.document)) : "",
    phone: s(c.phone),
    address_line1: line1,
    district: s(c.district),
    city_uf: cityUf,
    cep,
    full_address: [line1, join(" - ", c.district, cityUf), cep ? `CEP ${cep}` : ""]
      .filter(Boolean)
      .join("\n"),
  };
}

/** Registro de exemplo da prévia do editor. */
export const LABEL_SAMPLE: Record<LabelSource, LabelValues> = {
  products: productLabelValues({
    name: "Camiseta Algodão Azul M",
    sku: "CAM-AZ-M",
    gtin: "7891234567895",
    priceCents: 4990,
    unit: "UN",
    ncm: "61091000",
    location: "A-01-03",
    shortDescription: "100% algodão",
  }),
  contacts: contactLabelValues({
    name: "Maria da Silva",
    tradeName: null,
    code: "C-001",
    document: "12345678909",
    phone: "(11) 99999-0000",
    street: "Rua das Flores",
    number: "123",
    complement: "Apto 45",
    district: "Centro",
    city: "São Paulo",
    uf: "SP",
    cep: "01001000",
  }),
};

/** Texto de um elemento de texto/campo para um registro ("" = nada a imprimir). */
export function labelElementText(e: LabelElement, values: LabelValues): string {
  if (e.type === "text") return e.text;
  if (e.type !== "field") return "";
  const v = Object.hasOwn(values, e.field) ? values[e.field]! : "";
  return v ? `${e.prefix}${v}` : "";
}

/** Linhas de um texto/campo: o máximo do campo, ou quantas cabem na altura do texto fixo (1 a 4). */
export function labelElementLines(e: Extract<LabelElement, { type: "text" | "field" }>): number {
  if (e.type === "field") return Math.min(LABEL_LIMITS.maxLines, Math.max(1, e.maxLines));
  const heightPt = (e.h * 72) / 254;
  return Math.min(LABEL_LIMITS.maxLines, Math.max(1, Math.floor(heightPt / (e.fontSize * 1.2))));
}

// ---------- códigos de barras ----------

const EAN_L = [
  "0001101",
  "0011001",
  "0010011",
  "0111101",
  "0100011",
  "0110001",
  "0101111",
  "0111011",
  "0110111",
  "0001011",
];
const EAN_G = EAN_L.map((c) =>
  [...c]
    .map((b) => (b === "1" ? "0" : "1"))
    .reverse()
    .join(""),
);
const EAN_R = EAN_L.map((c) => [...c].map((b) => (b === "1" ? "0" : "1")).join(""));
const EAN_PARITY = [
  "LLLLLL",
  "LLGLGG",
  "LLGGLG",
  "LLGGGL",
  "LGLLGG",
  "LGGLLG",
  "LGGGLL",
  "LGLGLG",
  "LGLGGL",
  "LGGLGL",
];

/** EAN-13 em 95 módulos ("1" = barra). Exige 13 dígitos com o verificador certo. */
export function ean13Modules(code: string): string {
  if (!/^\d{13}$/.test(code) || gtinCheckDigit(code.slice(0, 12)) !== Number(code[12]))
    throw new DomainError("invalid_barcode", "EAN-13 inválido.");
  const d = [...code].map(Number);
  const parity = EAN_PARITY[d[0]!]!;
  let out = "101";
  for (let i = 1; i <= 6; i++) out += (parity[i - 1] === "L" ? EAN_L : EAN_G)[d[i]!]!;
  out += "01010";
  for (let i = 7; i <= 12; i++) out += EAN_R[d[i]!]!;
  return `${out}101`;
}

// Larguras (barra, espaço, barra, espaço, barra, espaço) dos 107 símbolos do Code 128; 106 = parada (7 elementos).
// prettier-ignore
export const CODE128_PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];
const CODE128_START_B = 104;
const CODE128_STOP = 106;
/** Texto aceito no Code 128 (conjunto B, ASCII imprimível), até 48 caracteres. */
export const CODE128_TEXT = /^[\x20-\x7e]{1,48}$/;

/** Símbolos (valores) do Code 128 B com o dígito verificador: início, dados, verificador e parada. */
export function code128Values(text: string): number[] {
  if (!CODE128_TEXT.test(text)) throw new DomainError("invalid_barcode", "Texto inválido para Code 128.");
  const data = [...text].map((ch) => ch.charCodeAt(0) - 32);
  const check = data.reduce((acc, v, i) => acc + v * (i + 1), CODE128_START_B) % 103;
  return [CODE128_START_B, ...data, check, CODE128_STOP];
}

/** Code 128 B em módulos ("1" = barra), com 2 módulos de barra final da parada já incluídos. */
export function code128Modules(text: string): string {
  let out = "";
  for (const v of code128Values(text)) {
    const p = CODE128_PATTERNS[v]!;
    for (let i = 0; i < p.length; i++) out += (i % 2 === 0 ? "1" : "0").repeat(Number(p[i]));
  }
  return out;
}

export type Barcode = { symbology: "ean13" | "code128"; modules: string; text: string; data: string };

/**
 * Código de barras do valor de um campo. EAN-13 aceita GTIN-13 e UPC-A (12 dígitos, com 0 à esquerda); valor fora
 * disso cai para Code 128. Valor vazio ou impossível de codificar = null (o elemento fica em branco).
 */
export function barcodeFor(symbology: "ean13" | "code128", value: string): Barcode | null {
  const v = value.trim();
  if (!v) return null;
  if (symbology === "ean13") {
    const code = /^\d{12}$/.test(v) ? `0${v}` : v;
    if (/^\d{13}$/.test(code) && gtinCheckDigit(code.slice(0, 12)) === Number(code[12]))
      return { symbology: "ean13", modules: ean13Modules(code), text: code, data: code };
  }
  if (!CODE128_TEXT.test(v)) return null;
  return { symbology: "code128", modules: code128Modules(v), text: v, data: v };
}

// ---------- ZPL ----------

/** Décimos de mm → pontos da impressora. */
export const mm10ToDots = (v: number, dpi: number) => Math.round((v * dpi) / 254);

/**
 * Campo de texto do ZPL em hexadecimal (`^FH`, indicador `_`): tudo que não é letra ASCII, dígito ou espaço vira
 * `_XX` dos bytes UTF-8 (`^CI28`). Assim `^`, `~` e quebras de linha nunca viram comandos.
 */
export function zplHex(text: string): string {
  let out = "";
  for (const b of new TextEncoder().encode(text)) {
    const ch = String.fromCharCode(b);
    out += /[A-Za-z0-9 ]/.test(ch) ? ch : `_${b.toString(16).toUpperCase().padStart(2, "0")}`;
  }
  return out;
}

/** Code 128 no ZPL: caracteres que o `^BC` interpreta (`>`, `<`, `^`, `~`, `_`) ficam de fora. */
const ZPL_CODE128 = /^[A-Za-z0-9 \-.$/+%*:#,()=]{1,48}$/;

const ALIGN_ZPL: Record<LabelAlign, string> = { left: "L", center: "C", right: "R" };

/** Uma etiqueta em ZPL II (`^XA … ^XZ`). */
export function labelZpl(l: LabelLayout, values: LabelValues): string {
  const dpi = l.zplDpi;
  const d = (v: number) => mm10ToDots(v, dpi);
  const out = [`^XA^CI28^PW${d(l.width)}^LL${d(l.height)}^LH0,0`];
  for (const e of l.elements) {
    if (e.type === "text" || e.type === "field") {
      const text = labelElementText(e, values);
      if (!text) continue;
      const lines = labelElementLines(e);
      const hDots = Math.max(10, Math.round((e.fontSize * dpi) / 72));
      // Quebra de linha do texto vira `\&` (nova linha do ^FB), não um comando.
      const body = text
        .split("\n")
        .map((ln) => zplHex(ln))
        .join("\\&");
      out.push(
        `^FO${d(e.x)},${d(e.y)}^A0N,${hDots},${hDots}^FB${d(e.w)},${lines},0,${ALIGN_ZPL[e.align]},0^FH^FD${body}^FS`,
      );
    } else if (e.type === "barcode") {
      const v = Object.hasOwn(values, e.field) ? values[e.field]! : "";
      const bc = barcodeFor(e.symbology, v);
      if (!bc) continue;
      const hr = e.showText ? "Y" : "N";
      const textH = e.showText ? Math.round((8 * dpi) / 72) : 0;
      const barH = Math.max(10, d(e.h) - textH);
      if (bc.symbology === "ean13") {
        const module = Math.max(1, Math.floor(d(e.w) / 95));
        out.push(
          `^FO${d(e.x)},${d(e.y)}^BY${module},3,${barH}^BEN,${barH},${hr},N^FD${bc.data.slice(0, 12)}^FS`,
        );
      } else {
        if (!ZPL_CODE128.test(bc.data)) continue;
        const module = Math.max(1, Math.floor(d(e.w) / bc.modules.length));
        out.push(`^FO${d(e.x)},${d(e.y)}^BY${module},3,${barH}^BCN,${barH},${hr},N,N^FD${bc.data}^FS`);
      }
    } else if (e.type === "qrcode") {
      const v = (Object.hasOwn(values, e.field) ? values[e.field]! : "").slice(0, LABEL_LIMITS.maxQrData);
      if (!v) continue;
      // Versão automática; ampliação pelo tamanho (cada módulo ≈ tamanho/25 pontos numa versão 2).
      const mag = Math.min(10, Math.max(1, Math.floor(d(e.size) / 25)));
      out.push(`^FO${d(e.x)},${d(e.y)}^BQN,2,${mag}^FH^FDQA,${zplHex(v)}^FS`);
    } else {
      const t = Math.max(1, d(e.thickness));
      out.push(`^FO${d(e.x)},${d(e.y)}^GB${Math.max(t, d(e.w))},${Math.max(t, d(e.h))},${t}^FS`);
    }
  }
  out.push("^XZ");
  return out.join("\n");
}

/** Arquivo ZPL com uma etiqueta por cópia (no ZPL a folha não existe: a impressora usa rolo). */
export function labelsZpl(
  l: LabelLayout,
  records: readonly { values: LabelValues; copies: number }[],
): string {
  const parts: string[] = [];
  let size = 0;
  for (const r of records) {
    const one = labelZpl(l, r.values);
    // Conferido antes de juntar: o arquivo nunca chega a ser montado acima do teto.
    size += (one.length + 1) * r.copies;
    if (size > LABELS_MAX_OUTPUT_BYTES) labelsTooLarge();
    for (let i = 0; i < r.copies; i++) parts.push(one);
  }
  return `${parts.join("\n")}\n`;
}

// ---------- modelos prontos ----------

export type LabelPreset = { id: string; name: string; source: LabelSource; layout: LabelLayout };
const field = (
  f: string,
  x: number,
  y: number,
  w: number,
  h: number,
  o: Partial<{ fontSize: number; bold: boolean; align: LabelAlign; maxLines: number; prefix: string }> = {},
): LabelElement => ({
  type: "field",
  field: f,
  prefix: o.prefix ?? "",
  x,
  y,
  w,
  h,
  fontSize: o.fontSize ?? 9,
  bold: o.bold ?? false,
  align: o.align ?? "left",
  maxLines: o.maxLines ?? 1,
});
export const LABEL_PRESETS: readonly LabelPreset[] = [
  {
    id: "pimaco-6180",
    name: "Endereço — Pimaco 6180 (Carta, 3 × 10)",
    source: "contacts",
    layout: {
      width: 667,
      height: 254,
      page: {
        kind: "sheet",
        paper: "LETTER",
        columns: 3,
        rows: 10,
        marginTop: 127,
        marginLeft: 48,
        gapX: 31,
        gapY: 0,
      },
      zplDpi: 203,
      elements: [
        field("name", 20, 15, 627, 45, { bold: true, fontSize: 8 }),
        field("full_address", 20, 65, 627, 175, { fontSize: 7, maxLines: 3 }),
      ],
    },
  },
  {
    id: "pimaco-6182",
    name: "Endereço — Pimaco 6182 (Carta, 2 × 7)",
    source: "contacts",
    layout: {
      width: 1016,
      height: 339,
      page: {
        kind: "sheet",
        paper: "LETTER",
        columns: 2,
        rows: 7,
        marginTop: 212,
        marginLeft: 40,
        gapX: 47,
        gapY: 0,
      },
      zplDpi: 203,
      elements: [
        field("name", 40, 30, 936, 55, { bold: true, fontSize: 10 }),
        field("full_address", 40, 95, 936, 220, { fontSize: 9, maxLines: 3 }),
      ],
    },
  },
  {
    id: "gondola-a4",
    name: "Gôndola — produto com preço e EAN (A4, 3 × 8)",
    source: "products",
    layout: {
      width: 660,
      height: 330,
      page: {
        kind: "sheet",
        paper: "A4",
        columns: 3,
        rows: 8,
        marginTop: 160,
        marginLeft: 60,
        gapX: 0,
        gapY: 0,
      },
      zplDpi: 203,
      elements: [
        field("name", 30, 20, 600, 80, { bold: true, fontSize: 9, maxLines: 2 }),
        field("price", 30, 110, 600, 70, { bold: true, fontSize: 16 }),
        { type: "barcode", field: "gtin", symbology: "ean13", x: 30, y: 190, w: 400, h: 120, showText: true },
        field("sku", 440, 250, 190, 40, { fontSize: 7, align: "right" }),
      ],
    },
  },
  {
    id: "rolo-50x30",
    name: "Produto — rolo térmico 50 × 30 mm",
    source: "products",
    layout: {
      width: 500,
      height: 300,
      page: { kind: "roll" },
      zplDpi: 203,
      elements: [
        field("name", 20, 15, 460, 70, { bold: true, fontSize: 8, maxLines: 2 }),
        field("price", 20, 90, 460, 50, { bold: true, fontSize: 12 }),
        {
          type: "barcode",
          field: "sku",
          symbology: "code128",
          x: 20,
          y: 150,
          w: 460,
          h: 130,
          showText: true,
        },
      ],
    },
  },
];
